import type { SiteWithTemplate } from '../../types/template';
import { APP_CONFIG } from '../../config/app.config';

/**
 * Parse site config — may arrive as object or JSON string.
 * Returns the inner `settings` object, or the config itself, or an empty object.
 */
export function getConfig(site: Pick<SiteWithTemplate, 'config'>): Record<string, any> {
  let config: any = site.config;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch {
      return {};
    }
  }
  return config?.settings || config || {};
}

/**
 * Resolve the public URL for a site.
 * Prefers explicit `url`, then `subdomain`, then falls back to config.
 */
export function getSiteUrl(site: Pick<SiteWithTemplate, 'url' | 'subdomain' | 'config'>): string {
  if (site.url) return site.url;
  if (site.subdomain) return `https://${site.subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
  const config = getConfig(site);
  if (config.subdomain) return `https://${config.subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
  if (config.domain) return `https://${config.domain}`;
  return `https://unknown.${APP_CONFIG.SITES_DOMAIN}`;
}

/**
 * Derive a display title from config or fall back to subdomain.
 */
export function getSiteTitle(site: Pick<SiteWithTemplate, 'subdomain' | 'config'>): string {
  const config = getConfig(site);
  return config.title || config.subdomain || site.subdomain || 'Unnamed Site';
}

/**
 * Strip the `https://` prefix for compact display in table cells.
 */
export function getSiteDisplayUrl(site: Pick<SiteWithTemplate, 'url' | 'subdomain' | 'config'>): string {
  return getSiteUrl(site).replace('https://', '');
}
